import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";
import "swiper/css";
import RecommendSection from "./components/RecommendSection";
// 限时特惠 / 热门目的地（暂时注释，后期可能启用）
// import FlashDealsSection from "./components/FlashDealsSection";
// import HotDestinations from "./components/HotDestinations";
// 语音录音 store（麦克风按钮暂时隐藏，后期恢复时启用）
// import { useVoiceStore } from "@/store/useVoiceStore";
import UserBottomNav from "@/components/feature/UserBottomNav";
import EmptyState from "@/components/EmptyState";
import { fetchTourRecommend, type RecommendProduct } from "@/api/home";
import { getCachedLocation, getDefaultLocation } from "@/utils/location";
import { buildDetailPath } from "@/utils/detailNav";

/** 首页选中城市在 localStorage 中的存储 key */
export const SELECTED_CITY_KEY = "home_selected_city";
/**
 * 默认城市（用户未选择过时使用）
 * 采用「成都市」——后端推荐列表当前只有成都的数据，
 * 用户一旦在位置选择页选过城市，就以其选择为准（持久化在 localStorage）
 */
export const DEFAULT_CITY = "成都市";

/** 首页选中城市经纬度在 localStorage 中的存储 key（后端按经纬度查商品，必须持久化） */
export const SELECTED_LOC_KEY = "home_selected_location";

/** 首页使用的经纬度（后端以经纬度为主要查询条件） */
export interface SelectedLocation {
  latitude: number;
  longitude: number;
}

/**
 * 读取首页选中的经纬度
 *
 * 优先级：localStorage 已存（用户在位置选择页选过）→ 用它；
 *         否则用定位工具里的默认城市坐标（成都），保证接口有值可查。
 */
export function readSelectedLocation(): SelectedLocation {
  try {
    const raw = localStorage.getItem(SELECTED_LOC_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<SelectedLocation>;
      const lat = Number(parsed?.latitude);
      const lng = Number(parsed?.longitude);
      if (Number.isFinite(lat) && Number.isFinite(lng) && lat !== 0 && lng !== 0) {
        return { latitude: lat, longitude: lng };
      }
    }
  } catch {
    /* ignore */
  }

  // 兜底：定位缓存（用户已授权时会有真实坐标）
  const cached = getCachedLocation();
  if (cached && Number.isFinite(cached.latitude) && Number.isFinite(cached.longitude)) {
    return { latitude: cached.latitude, longitude: cached.longitude };
  }

  // 最终兜底：默认城市（成都）
  const fallback = getDefaultLocation();
  return { latitude: fallback.latitude, longitude: fallback.longitude };
}

const businessItems = [
  { icon: "ri-map-2-line", label: "热门线路", color: "#14B8A6", bg: "#E6F7F5", path: "/routes" },
  { icon: "ri-user-star-line", label: "专业导游", color: "#F59E0B", bg: "#FEF6E0", path: "/guides" },
  { icon: "ri-hotel-line", label: "精选酒店", color: "#2563EB", bg: "#EAF1FE", path: "/hotels" },
  { icon: "ri-car-line", label: "省心租车", color: "#0EA5E9", bg: "#E4F4FD", path: "/cars" },
];

/** Banner 轮播项（由接口数据映射而来） */
interface BannerItem {
  id: number;
  /** 背景图 */
  url: string;
  /** 主标题 */
  title: string;
  /** 副标题 */
  subtitle: string;
  /** 左上角标签 */
  tag: string;
  /** 原始接口数据（点击跳转详情页时透传） */
  raw: RecommendProduct;
}



/* ── 首页「美伊AI助手」卡片边框流光参数（在 JS 内预计算，保证各机型渲染一致） ──
   SEG_COUNT   段数：每段占路径 2.5%，16 段合计 40% 为一束光的长度
   DURATION    单圈时长（秒）：位移与变色共用，保证整束光同色
   SEG_DELAY   每段相位差（秒）= 2.5% × DURATION / 100，段间首尾相接形成连续光束
   TURN_DELAY  第二束光整体滞后（秒）：DURATION / 2 = 半圈
   OPACITY_MAX 头部透明度，随后逐段递减至尾部消失
   WIDTH_MAX   头部线宽 px，沿拖尾递减 */
const AI_ORBIT_SEG_COUNT = 16;
const AI_ORBIT_DURATION = 3.2;
const AI_ORBIT_SEG_DELAY = (AI_ORBIT_DURATION / 100) * 2.5;
const AI_ORBIT_TURN_DELAY = AI_ORBIT_DURATION / 2;
const AI_ORBIT_OPACITY_MAX = 0.8;
const AI_ORBIT_WIDTH_MAX = 2.6;

/** 生成一束光的 16 段行内样式；turnOffset 为 0 表示主光 */
const buildOrbitSegments = (turnOffset: number) =>
  Array.from({ length: AI_ORBIT_SEG_COUNT }, (_, i) => ({
    i,
    style: {
      strokeOpacity: Number((AI_ORBIT_OPACITY_MAX - i * 0.058).toFixed(3)),
      strokeWidth: Number((AI_ORBIT_WIDTH_MAX - i * 0.08).toFixed(2)),
      animationDuration: `${AI_ORBIT_DURATION}s`,
      animationDelay: `${(turnOffset + i * AI_ORBIT_SEG_DELAY).toFixed(3)}s`,
    } as React.CSSProperties,
  }));

const AI_ORBIT_SEGMENTS = buildOrbitSegments(0);
const AI_ORBIT_SEGMENTS_TRAIL = buildOrbitSegments(AI_ORBIT_TURN_DELAY);


export default function HomePage() {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const [bannerIdx, setBannerIdx] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  // ★ Banner 轮播数据（来自接口 GET /home/index/tourRecommend）
  const [banners, setBanners] = useState<BannerItem[]>([]);
  // Banner 是否已加载完成（用于区分「加载中」与「暂无商品」）
  const [bannerLoaded, setBannerLoaded] = useState(false);
  // Banner Swiper 实例（用于圆点点击切换 slideToLoop）
  const bannerSwiperRef = useRef<SwiperType | null>(null);

  // 当前城市：持久化到 localStorage，保证刷新/切页后依然回显上次选中的地址
  // 优先级：location-select 返回的 state → localStorage 已存 → 默认值「成都市」
  const stateFromSelect = routerLocation.state as Record<string, unknown> | null;

  const [currentCity, setCurrentCity] = useState<string>(() => {
    // 从位置选择页返回时，直接用其 state，避免与 localStorage 短暂不一致
    if (stateFromSelect?.selectedCity) {
      return String(stateFromSelect.selectedCity);
    }
    try {
      return localStorage.getItem(SELECTED_CITY_KEY) || DEFAULT_CITY;
    } catch {
      return DEFAULT_CITY;
    }
  });

  // ★ 当前经纬度：同样由 state 驱动（后端以经纬度为主要查询条件，
  //   必须与城市同步更新，否则会出现「换了城市但商品还是老城市的」）
  const [currentLocation, setCurrentLocation] = useState<SelectedLocation>(() => {
    const lat = Number(stateFromSelect?.selectedLat);
    const lng = Number(stateFromSelect?.selectedLng);
    if (Number.isFinite(lat) && Number.isFinite(lng) && lat !== 0 && lng !== 0) {
      return { latitude: lat, longitude: lng };
    }
    return readSelectedLocation();
  });

  // 监听后续从 location-select 返回的 state（组件已挂载时再次选择城市）
  useEffect(() => {
    if (!stateFromSelect?.selectedCity) return;

    const city = String(stateFromSelect.selectedCity);
    setCurrentCity(city);

    const lat = Number(stateFromSelect.selectedLat);
    const lng = Number(stateFromSelect.selectedLng);
    const hasCoord =
      Number.isFinite(lat) && Number.isFinite(lng) && lat !== 0 && lng !== 0;
    if (hasCoord) {
      setCurrentLocation({ latitude: lat, longitude: lng });
    }

    try {
      // ★ 同步持久化，确保刷新后仍显示最新选中的地址与坐标
      localStorage.setItem(SELECTED_CITY_KEY, city);
      if (hasCoord) {
        localStorage.setItem(
          SELECTED_LOC_KEY,
          JSON.stringify({ latitude: lat, longitude: lng }),
        );
      }
    } catch {
      /* ignore */
    }
  }, [
    stateFromSelect?.selectedCity,
    stateFromSelect?.selectedLat,
    stateFromSelect?.selectedLng,
  ]);

  // ★ 拉取 Banner 数据：城市或坐标变化时重新请求（后端按经纬度返回推荐）
  useEffect(() => {
    let cancelled = false;

    const loadBanners = async () => {
      // 切换城市重新请求时，先回到「加载中」态
      setBannerLoaded(false);
      try {
        const list = await fetchTourRecommend({
          city: currentCity,
          longitude: currentLocation.longitude,
          latitude: currentLocation.latitude,
        });
        if (cancelled) return;

        // 映射为 Banner 结构：封面图 / 名称 / 描述 / 价格文案作为角标
        const mapped: BannerItem[] = list.slice(0, 5).map((item) => ({
          id: item.id,
          url: item.cover_image,
          title: item.name,
          subtitle: item.desc || item.distance_text || "",
          tag: item.price_text || "热门推荐",
          raw: item,
        }));

        setBanners(mapped);
        // 数据变化后重置轮播下标，避免越界
        setBannerIdx(0);
      } catch (e) {
        console.error("[HomePage] 加载 Banner 失败:", e);
      } finally {
        if (!cancelled) setBannerLoaded(true);
      }
    };

    loadBanners();
    return () => {
      cancelled = true;
    };
  }, [currentCity, currentLocation.latitude, currentLocation.longitude]);

  // ★ 按住录音状态与处理函数（微信式交互）
  //   麦克风按钮暂时隐藏（见下方 Search Bar），此整块逻辑一并保留，后期恢复按钮时启用
  /*
  const {
    isPressRecording,
    deviceStatus,
    startPressToRecord,
    stopPressToRecord,
  } = useVoiceStore();
  const [isHolding, setIsHolding] = useState(false);
  const holdStartRef = useRef<number>(0);
  const holdTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMicPressStart = async () => {
    if (deviceStatus !== "available") return;
    setIsHolding(true);
    holdStartRef.current = Date.now();
    holdTimerRef.current = setTimeout(async () => {
      const ok = await startPressToRecord();
      if (!ok) setIsHolding(false);
    }, 150);
  };

  const handleMicPressEnd = async (cancelled = false) => {
    if (!isHolding) return;
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
    const heldMs = Date.now() - holdStartRef.current;
    if (heldMs < 300 && !cancelled) cancelled = true;
    setIsHolding(false);
    await stopPressToRecord(cancelled);
  };

  const handleMicPressCancel = () => {
    handleMicPressEnd(true);
  };
  */

  return (
    <div
      className="min-h-screen relative"
      style={{ fontFamily: "'Inter', 'SF Pro Text', sans-serif", paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 88px)" }}
    >
      {/* ─── 顶部渐变氛围底（淡蓝绿 → 白，仅顶部区域可见） ─── */}
      <div className="home-hero-bg" aria-hidden="true" />

      {/* ─── Top Bar ───
          布局：左（城市，自适应但限宽）/ 中（品牌，占剩余空间居中）/ 右（客服，固定）
          ★ 中间用 flex-1 占据剩余空间，不再用 absolute 相对屏幕居中，
            从根本上避免城市名变长时被挤压或遮挡 */}
      <div
        className="px-5 pb-3 sticky top-0 z-40 flex items-center gap-1 relative"
        style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 16px)", background: "rgba(255,255,255,0.1)", backdropFilter: "blur(12px)" }}
      >
        <button
          onClick={() => navigate("/location-select", { state: { from: "/home" } })}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full cursor-pointer active:scale-95 transition-all flex-shrink-0"
          style={{ background: "rgba(255,255,255,0.75)", maxWidth: "40%" }}
        >
          <i className="ri-map-pin-2-fill flex-shrink-0" style={{ color: "#12B0A0", fontSize: 15 }} />
          {/* ★ 城市名超长时省略，避免把顶栏撑爆挤压中间品牌名 */}
          <span className="text-sm font-semibold truncate" style={{ color: "#1F2937" }}>{currentCity}</span>
          <i className="ri-arrow-down-s-line flex-shrink-0" style={{ color: "#9CA3AF", fontSize: 13 }} />
        </button>

        {/* 中间品牌名
            ★ 原先用 absolute left-1/2 相对「屏幕」居中，但左右两侧宽度是动态的
              （如城市名较长时），会把品牌名挤压/遮挡。
              改为 flex-1 + 自身居中：在「左侧按钮」与「右侧按钮」之间的剩余空间内居中，
              并加 min-w-0 + truncate 约束，宽度不足时优雅省略，绝不重叠。 */}
        <span
          className="flex-1 min-w-0 text-center text-base font-bold truncate pointer-events-none px-2"
          style={{ color: "#111827", fontFamily: "'Microsoft YaHei', '微软雅黑', sans-serif" }}
        >
          Mex Claw
        </span>

        <div className="flex items-center gap-2 flex-shrink-0">
          {/* 铃铛通知入口（暂时注释，后期可能启用） */}
          {/* <button
            onClick={() => navigate("/messages")}
            className="relative w-9 h-9 flex items-center justify-center rounded-full cursor-pointer"
            style={{ background: "#F3F4F6" }}
          >
            <i className="ri-notification-3-line" style={{ color: "#374151", fontSize: 19 }} />
            <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full border-2 border-white" style={{ background: "#EF4444" }} />
          </button> */}
          <button
            onClick={() => navigate("/ai-voice")}
            className="w-9 h-9 flex items-center justify-center rounded-full cursor-pointer"
            style={{ background: "rgba(255,255,255,0.75)" }}
          >
            <i className="ri-customer-service-2-line" style={{ color: "#12B0A0", fontSize: 19 }} />
          </button>
        </div>
      </div>

      {/* ─── Hero Banner（数据来自 /home/index/tourRecommend）
             自动轮播 + 支持手指左右滑动切换，滑动手势结束后继续自动播放 ─── */}
      <div className="relative mx-5 mt-4 rounded-3xl overflow-hidden" style={{ height: 205, background: "#F3F4F6", boxShadow: "0 8px 22px rgba(15,42,68,0.14)" }}>
        {banners.length > 0 ? (
          <>
            <Swiper
              modules={[Autoplay]}
              autoplay={{ delay: 4000, disableOnInteraction: false }}
              loop={banners.length > 1}
              slidesPerView={1}
              spaceBetween={0}
              style={{ height: 205 }}
              onSwiper={(sw) => (bannerSwiperRef.current = sw)}
              onSlideChange={(sw) => setBannerIdx(sw.realIndex)}
            >
              {banners.map((b) => (
                <SwiperSlide key={b.id}>
                  <div
                    className="relative w-full h-full cursor-pointer"
                    onClick={() => {
                      // ★ 按商品类型跳对应详情页（原先写死 route-detail-product，非线路类会走错页面）
                      const path = buildDetailPath(b.raw?.type, b.id);
                      if (!path) return;
                      navigate(path, {
                        // query 保证刷新/分享不丢 ID；state 携带完整对象供详情页首屏直用
                        // 各详情页约定的 ID 字段名不同，这里统一都带上
                        state: {
                          routeId: b.id,
                          guideId: b.id,
                          hotelId: b.id,
                          carId: b.id,
                          productId: b.id,
                          product: b.raw,
                        },
                      });
                    }}
                  >
                    <img
                      src={b.url}
                      alt={b.title}
                      draggable={false}
                      className="w-full h-full object-cover object-top"
                    />
                    <div
                      className="absolute inset-0"
                      style={{ background: "linear-gradient(to top, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.15) 60%, transparent 100%)" }}
                    />
                    {/* 底部信息区：标题 / 副标题 分行，价格标签在下方 */}
                    <div className="absolute bottom-9 left-4 right-4 flex flex-col gap-1.5">
                      <div className="min-w-0">
                        <div className="text-white font-bold leading-tight truncate" style={{ fontSize: 21 }}>
                          {b.title}
                        </div>
                        {b.subtitle && (
                          <div className="text-white text-xs mt-1 truncate" style={{ opacity: 0.85 }}>
                            {b.subtitle}
                          </div>
                        )}
                      </div>
                      {/* 价格标签：左下角橙色胶囊 */}
                      {b.tag && (
                        <span
                          className="self-start px-3 py-1 rounded-full text-xs font-semibold text-white whitespace-nowrap"
                          style={{
                            background: "linear-gradient(90deg, #FF8A3D 0%, #FF6B2C 100%)",
                            boxShadow: "0 2px 8px rgba(255,107,44,0.35)",
                          }}
                        >
                          {b.tag}
                        </span>
                      )}
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>

            {/* Dots：底部横排居中，当前项拉长；点击可跳转指定项 */}
            <div className="absolute bottom-2.5 left-0 right-0 z-10 flex items-center justify-center gap-1.5 pointer-events-none">
              {banners.map((_, i) => (
                <button
                  key={i}
                  onClick={() => bannerSwiperRef.current?.slideToLoop(i)}
                  className="rounded-full cursor-pointer transition-all pointer-events-auto"
                  style={{
                    width: i === bannerIdx ? 18 : 6,
                    height: 6,
                    background: i === bannerIdx ? "#fff" : "rgba(255,255,255,0.5)",
                  }}
                />
              ))}

            </div>
          </>
        ) : bannerLoaded ? (
          /* 加载完成但无数据 */
          <EmptyState text="暂无商品" size={72} className="w-full h-full" />
        ) : (
          /* 加载中 */
          <div className="w-full h-full flex items-center justify-center">
            <span className="text-sm" style={{ color: "#9CA3AF" }}>加载中...</span>
          </div>
        )}
      </div>

      {/* ─── Search Bar ─── */}
      <div className="mx-5 mt-4">
        <div
          className="flex items-center gap-3 px-4 rounded-2xl"
          style={{ height: 50, background: "#fff", border: "1.5px solid #E5E7EB" }}
        >
          {/* ★ 搜索框主体：点击整体跳转到 AI 语音页（不做站内输入，
              用展示文字代替 input，避免移动端弹出键盘后又跳页的闪烁） */}
          <div
            onClick={() => navigate("/ai-voice")}
            className="flex-1 flex items-center gap-3 cursor-pointer min-w-0"
          >
            <i className="ri-search-line" style={{ color: "#B6BEC9", fontSize: 18 }} />
            <span className="truncate" style={{ fontSize: 14, color: "#9AA3AF" }}>
              搜索目的地、景点、酒店、玩法
            </span>
          </div>
          {/* 麦克风「按住说话」按钮（暂时注释，后期可能会用）
          <button
            onMouseDown={handleMicPressStart}
            onMouseUp={() => handleMicPressEnd(false)}
            onMouseLeave={handleMicPressCancel}
            onTouchStart={(e) => { e.preventDefault(); handleMicPressStart(); }}
            onTouchEnd={(e) => { e.preventDefault(); handleMicPressEnd(false); }}
            onTouchCancel={handleMicPressCancel}
            onContextMenu={(e) => e.preventDefault()}
            className="w-8 h-8 flex items-center justify-center rounded-xl flex-shrink-0 cursor-pointer select-none active:scale-90 transition-all"
            style={{
              background: (isHolding || isPressRecording) ? "#FEE2E2" : "#EFF6FF",
              border: `1px solid ${(isHolding || isPressRecording) ? "#FECACA" : "transparent"}`,
            }}
          >
            <i
              className={isHolding || isPressRecording ? "ri-stop-circle-fill" : "ri-mic-line"}
              style={{
                color: (isHolding || isPressRecording) ? "#EF4444" : "#1E3A8A",
                fontSize: 17,
              }}
            />
          </button>
          */}
        </div>
      </div>

      {/* ─── Business Entry Icons ─── */}
      <div className="mx-5 mt-5 grid grid-cols-4 gap-3">
        {businessItems.map((item) => (
          <button
            key={item.label}
            onClick={() => navigate(item.path)}
            className="flex flex-col items-center gap-2 py-3.5 rounded-2xl cursor-pointer transition-all active:scale-95"
            style={{ background: "transparent" }}
          >
            <div
              className="w-11 h-11 flex items-center justify-center rounded-2xl"
              style={{ background: item.bg }}
            >
              <i className={item.icon} style={{ color: item.color, fontSize: 22 }} />
            </div>
            <span className="text-xs font-medium" style={{ color: "#374151" }}>{item.label}</span>
          </button>
        ))}
      </div>

      {/* ─── AI Entry Banner ─── */}
      <div className="mx-5 mt-4">
        <button
          onClick={() => navigate("/ai-voice")}
          className="ai-entry-banner w-full flex items-center gap-3 pl-3 pr-3 py-2.5 rounded-2xl cursor-pointer transition-all active:scale-[0.98] relative"
          style={{ minHeight: 82 }}
        >
          {/* 边框流光：两束光沿边框绕圈，每束呈“电筒照射”状——头部亮、向后渐隐
              所有数值在 JS 内算好后直接写入行内样式，避免 CSS 变量参与 calc() 在不同手机的差异 */}
          <svg className="ai-orbit-svg" aria-hidden="true" preserveAspectRatio="none">
            {/* 光 1：16 段由亮到灭，拼成渐隐拖尾 */}
            {AI_ORBIT_SEGMENTS.map((s) => (
              <rect key={`main-${s.i}`} className="ai-orbit-seg" x="1" y="1" pathLength={100} style={s.style} />
            ))}
            {/* 光 2：与主光同形态，相位滞后半圈，形成一前一后追逐 */}
            {AI_ORBIT_SEGMENTS_TRAIL.map((s) => (
              <rect key={`trail-${s.i}`} className="ai-orbit-seg" x="1" y="1" pathLength={100} style={s.style} />
            ))}
          </svg>

          {/* 左侧：AI 形象（背书包看书的熊猫，对齐 UI 原型；已抠白底为透明 PNG）
               熊猫图高度超出卡片，靠负边距溢出到上下描边之外（原型即是此效果），
               配合卡片去掉 overflow-hidden 才能正常显示 */}
          <div
            className="relative z-10 flex-shrink-0 flex items-center justify-center"
            style={{ width: 68, height: 68, marginTop: -4, marginBottom: -4 }}
          >
            <img src="/ai-panda.png" alt="" className="w-full h-full object-contain" />
          </div>

          {/* 中间：标题 + 副标题（左对齐，贴近原型）
               ★ 不加 truncate：文案较长，省略号会截断原型要求的完整句子 */}
          <div className="relative z-10 min-w-0 flex-1 text-left">
            <div className="font-bold text-[15px] leading-tight whitespace-nowrap" style={{ color: "#1F2937" }}>
              美伊AI助手
            </div>
            <div className="mt-1 whitespace-nowrap" style={{ fontSize: 11, color: "#6B7280" }}>
              说出想法，即刻生成贴心行程
            </div>
          </div>

          {/* 右侧：青绿渐变按钮（白字） */}
          <div
            className="relative z-10 flex items-center justify-center px-3.5 rounded-full cursor-pointer flex-shrink-0"
            style={{
              height: 40,
              background: "linear-gradient(135deg, #2ED9C3 0%, #16B8D6 55%, #2E9BE0 100%)",
              boxShadow: "0 4px 12px rgba(24,178,197,0.32)",
            }}
          >
            <span className="text-sm font-semibold whitespace-nowrap text-white">立即规划</span>
          </div>
        </button>
      </div>

      {/* ─── Content Sections ─── */}
      <div ref={scrollRef}>
        {/* 城市与坐标由这里传入（而非组件内部读 localStorage）：
            父组件写 localStorage 的 effect 晚于子组件挂载，子组件自行读取会拿到旧值 */}
        <RecommendSection
          city={currentCity}
          latitude={currentLocation.latitude}
          longitude={currentLocation.longitude}
        />
        {/* 限时特惠（暂时注释，后期可能启用） */}
        {/* <FlashDealsSection /> */}
        {/* 热门目的地（暂时注释，后期可能启用） */}
        {/* <HotDestinations /> */}
      </div>

      {/* ─── 底部导航 ─── */}
      <UserBottomNav />
    </div>
  );
}
